import React from 'react';
import { UseFormReturn } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';

import { DAY_OF_WEEK } from 'src/features/membershipType/types';
import { DAY_OF_WEEK_TRANSLATIONS_MAP } from 'src/features/membershipType/utils/constants';
import { CustomAllowedTimeFormSchemaValues } from 'src/features/membershipType/formSchema';
import { allowedTimesMessages } from 'src/features/membershipType/utils/messages';
import { Form, FormControl, FormControlStack, FormField, FormItem, FormLabel, FormMessage } from 'src/registry/ui/Form';
import { Input } from 'src/registry/ui/Input';
import { workingHoursMessages } from 'src/features/workingHours/utils/messages.ts';
import { SelectContent, SelectItem, SelectTrigger, SelectValue, Select } from 'src/registry/ui/Select.tsx';
import MultiSelectFormField from 'src/registry/components/forms/MultiSelectFormField';

type AllowedTimeFormProps = {
    form: UseFormReturn<CustomAllowedTimeFormSchemaValues>;
    onSubmit: (values: CustomAllowedTimeFormSchemaValues) => void;
    isEdit: boolean;
};

function AllowedTimeForm({ onSubmit, form, isEdit }: AllowedTimeFormProps) {
    const { formatMessage } = useIntl();

    const options = React.useMemo(
        () => [
            {
                id: DAY_OF_WEEK.MONDAY,
                name: formatMessage(workingHoursMessages.monday),
            },
            {
                id: DAY_OF_WEEK.TUESDAY,
                name: formatMessage(workingHoursMessages.tuesday),
            },
            {
                id: DAY_OF_WEEK.WEDNESDAY,
                name: formatMessage(workingHoursMessages.wednesday),
            },
            {
                id: DAY_OF_WEEK.THURSDAY,
                name: formatMessage(workingHoursMessages.thursday),
            },
            {
                id: DAY_OF_WEEK.FRIDAY,
                name: formatMessage(workingHoursMessages.friday),
            },
            {
                id: DAY_OF_WEEK.SATURDAY,
                name: formatMessage(workingHoursMessages.saturday),
            },
            {
                id: DAY_OF_WEEK.SUNDAY,
                name: formatMessage(workingHoursMessages.sunday),
            },
        ],
        [formatMessage],
    );

    return (
        <Form form={form} onSubmit={form.handleSubmit(onSubmit)} variant="dialog">
            {isEdit ? (
                <FormField
                    control={form.control}
                    name="day_of_week"
                    render={({ field }) => {
                        return (
                            <FormItem variant="dialog">
                                <FormLabel variant="dialog" required>
                                    {formatMessage(allowedTimesMessages.dayOfWeek)}
                                </FormLabel>
                                <FormControlStack>
                                    <FormControl>
                                        <Select
                                            onValueChange={(value) => {
                                                field.onChange([value]);
                                            }}
                                            value={field.value?.toString()}
                                        >
                                            <SelectTrigger className="w-full">
                                                <SelectValue
                                                    placeholder={`${formatMessage(allowedTimesMessages.dayOfWeek)}...`}
                                                />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {Object.values(DAY_OF_WEEK).map((option) => (
                                                    <SelectItem key={option} value={option}>
                                                        {formatMessage(DAY_OF_WEEK_TRANSLATIONS_MAP[option])}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </FormControl>
                                    <FormMessage />
                                </FormControlStack>
                            </FormItem>
                        );
                    }}
                />
            ) : (
                <MultiSelectFormField<CustomAllowedTimeFormSchemaValues>
                    form={form}
                    required
                    name="day_of_week"
                    label={formatMessage(allowedTimesMessages.dayOfWeek)}
                    options={options}
                />
            )}

            <FormField
                control={form.control}
                name="from_time"
                render={({ field }) => (
                    <FormItem variant="dialog">
                        <FormLabel variant="dialog" required>
                            <FormattedMessage {...allowedTimesMessages.from} />
                        </FormLabel>
                        <FormControlStack>
                            <FormControl>
                                <Input type="time" {...field} value={field.value ?? ''} />
                            </FormControl>
                            <FormMessage />
                        </FormControlStack>
                    </FormItem>
                )}
            />
            <FormField
                control={form.control}
                name="to_time"
                render={({ field }) => (
                    <FormItem variant="dialog">
                        <FormLabel variant="dialog" required>
                            <FormattedMessage {...allowedTimesMessages.to} />
                        </FormLabel>
                        <FormControlStack>
                            <FormControl>
                                <Input type="time" {...field} value={field.value ?? ''} />
                            </FormControl>
                            <FormMessage />
                        </FormControlStack>
                    </FormItem>
                )}
            />
        </Form>
    );
}

export default AllowedTimeForm;
